import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone';
import { EVENT_EXPIRY_PRECISION, isEventExpired } from '@/js/core/utils/event-expiry';
import {
  calendarVisibleDateRange,
  getCurrentCalendarDateKey,
  hasZoom,
  locationWithZoom,
} from '@/js/core/utils/calendar-events';
import {
  CALENDAR_EVENT_DISPLAY_MODES,
  calendarDateLabel,
  calendarEventCurrentYear,
  calendarEventDateKey,
  calendarEventDisplayTime,
  calendarEventEndDateKey,
  calendarEventEndTime,
  calendarEventSortTimestamp,
  calendarEventStartTime,
  calendarRangeDateLabel,
  dateKeyInfo,
} from '@/js/core/utils/calendar-event-time';

const DAY_MS = 24 * 60 * 60 * 1000;
const VISIBLE_CALENDAR_EVENTS_LIMIT = 5;

export function calendarEventPlacements(events, { timezone = DEFAULT_TIMEZONE } = {}) {
  return sortProjectedEvents(
    events.flatMap(event =>
      occurrenceGroupsForEvent(event, timezone).map(occurrences =>
        eventForOccurrences(event, occurrences, timezone)
      )
    )
  );
}

/**
 * @param {Array<object>} events
 * @param {{ activeRange?: object, timezone?: string, showPastEvents?: boolean,
 *           hideExpiredEvents?: boolean }} options
 *   `hideExpiredEvents` retires a row as soon as its end time passes rather than at the end of its
 *   day. Showing past events overrides it, so a retired row is still reachable behind the toggle.
 */
export function calendarEventListItems(
  events,
  {
    activeRange,
    timezone = DEFAULT_TIMEZONE,
    showPastEvents = false,
    hideExpiredEvents = false,
  } = {}
) {
  const visibleRange = visibleMonthRange(activeRange, timezone, showPastEvents);

  // Group each event's occurrences into consecutive-day runs, then emit one row
  // per run. Consecutive days collapse to a span (e.g. "27–30"); a weekly event
  // renders one row per date rather than a single merged "1–29" span.
  // Doesn't group Reviews.
  const rows = sortProjectedEvents(
    events.flatMap(event =>
      occurrenceGroups(occurrencesForEvent(event), timezone, {
        skipRunMerging: event.filter_group === 'reviews',
      }).flatMap(occurrences =>
        listItemsForOccurrenceRun(event, occurrences, visibleRange, timezone)
      )
    )
  );

  if (!hideExpiredEvents || showPastEvents) {
    return rows;
  }

  return rows.filter(
    row => !isEventExpired(row, { timezone, precision: EVENT_EXPIRY_PRECISION.time })
  );
}

/**
 * Project events into list rows for an arbitrary date window, one row per start.
 *
 * Unlike the month-based sibling above, rows are never split at a month boundary, repeats
 * list one row per instance rather than collapsing into a span, occurrences sharing a day and
 * a start time merge so an event booked into several rooms is one row, and a date-only span
 * becomes one row per day rather than a single "12–14".
 *
 * @param {Array<object>} events
 * @param {{ startKey: string, endKey: string, timezone?: string }} options
 * @returns {Array<object>}
 */
export function calendarEventListItemsInWindow(
  events,
  { startKey, endKey, timezone = DEFAULT_TIMEZONE } = {}
) {
  if (!startKey || !endKey) {
    return [];
  }

  return sortProjectedEvents(
    events.flatMap(event =>
      occurrencesByStartSlot(occurrencesForEvent(event), timezone).flatMap(occurrences => {
        const slotRange = rangeForOccurrences(occurrences, timezone);

        if (!rangesOverlap(slotRange.startKey, slotRange.endKey, startKey, endKey)) {
          return [];
        }

        const projected = eventForOccurrences(event, occurrences, timezone);
        // Clamped so an event already under way reads from today, not its passed start.
        const segment = {
          startKey: maxDateKey(slotRange.startKey, startKey),
          endKey: minDateKey(slotRange.endKey, endKey),
        };

        return daySegments(projected, segment).map(daySegment => ({
          ...projectedEventForVisibleSegment(projected, daySegment),
          // The shared id is event plus date range, which collides on two starts in one day.
          id: `${event.id}:${daySegment.startKey}:${daySegment.endKey}:${calendarEventStartTime(projected)}`,
        }));
      })
    )
  );
}

/**
 * Bucket an event's occurrences by the day and time they start.
 *
 * @param {Array<object>} occurrences
 * @param {string} timezone
 * @returns {Array<Array<object>>}
 */
/**
 * Split a date-only span into one segment per day.
 *
 * A date-only row carries no time of its own — projectedEventForVisibleSegment gives each
 * segment midnight to end of day — so a span breaks into days without inventing anything, and
 * each day stands or expires on its own. A timed span has one real start and end, so it stays
 * whole; repeating those times on every day would put the wrong time on all but the first.
 *
 * @param {object} projected
 * @param {{ startKey: string, endKey: string }} segment
 * @returns {Array<{ startKey: string, endKey: string }>}
 */
function daySegments(projected, segment) {
  if (
    segment.startKey === segment.endKey ||
    projected.display_mode !== CALENDAR_EVENT_DISPLAY_MODES.dateOnly
  ) {
    return [segment];
  }

  const segments = [];

  for (let dayKey = segment.startKey; dayKey <= segment.endKey; dayKey = addDays(dayKey, 1)) {
    segments.push({ startKey: dayKey, endKey: dayKey });
  }

  return segments;
}

function occurrencesByStartSlot(occurrences, timezone) {
  const slots = new Map();

  for (const occurrence of sortOccurrences(occurrences)) {
    const slotKey = `${calendarEventRangeStartKey(occurrence, timezone)}:${calendarEventStartTime(occurrence)}`;

    if (!slots.has(slotKey)) {
      slots.set(slotKey, []);
    }

    slots.get(slotKey).push(occurrence);
  }

  return [...slots.values()];
}

/**
 * Report whether the month holds anything the past-events toggle would reveal.
 *
 * Asks the list itself rather than repeating its date rules: showing past events
 * can only add rows, so more rows means there is something to reveal.
 *
 * Takes hideExpiredEvents for the same reason: at that precision an event that ended earlier today
 * is past, and the toggle has to offer it even though the day it belongs to has not elapsed.
 *
 * @param {Array<object>} events
 * @param {{ activeRange?: object, timezone?: string, hideExpiredEvents?: boolean }} options
 * @returns {boolean}
 */
export function hasPastCalendarEvents(
  events,
  { activeRange, timezone = DEFAULT_TIMEZONE, hideExpiredEvents = false } = {}
) {
  const options = { activeRange, timezone, hideExpiredEvents };

  return (
    calendarEventListItems(events, { ...options, showPastEvents: true }).length >
    calendarEventListItems(events, { ...options, showPastEvents: false }).length
  );
}

export function calendarEventWeekPlacementsFromProjectedEvents(
  projectedEvents,
  weekDays,
  { limitDisplayedEvents = true, expandedDays = {} } = {}
) {
  const weekStartKey = weekDays[0]?.dateString || '';
  const weekEndKey = weekDays[weekDays.length - 1]?.dateString || '';
  const dayKeys = weekDays.map(day => day.dateString);
  const placements = sortProjectedEvents(
    projectedEvents
      .filter(event =>
        rangesOverlap(event.range_start_key, event.range_end_key, weekStartKey, weekEndKey)
      )
      .map(event => eventForWeek(event, weekStartKey, weekEndKey, dayKeys))
  );

  if (!limitDisplayedEvents) {
    return {
      placements,
      renderItems: placements.map(event => ({
        kind: 'event',
        event,
        hidden: false,
        crowded: false,
      })),
      hasOverflow: false,
    };
  }

  const eventsByDay = new Map(
    dayKeys.map(key => [key, placements.filter(event => event.day_keys.includes(key))])
  );

  const overflowDays = new Set(
    dayKeys.filter(key => eventsByDay.get(key).length > VISIBLE_CALENDAR_EVENTS_LIMIT)
  );

  const visibleIdsByDay = new Map(
    dayKeys.map(key => [
      key,
      new Set(
        eventsByDay
          .get(key)
          .slice(0, VISIBLE_CALENDAR_EVENTS_LIMIT)
          .map(event => event.id)
      ),
    ])
  );

  // A multi-day card is one element, so it is hidden only when every day it
  // touches hides it; making the top five of any single day keeps it visible
  // across its whole span. Hiding it whenever any day overflowed left columns
  // empty and made "show more" reveal nothing.
  const hiddenIds = new Set(
    placements
      .filter(event =>
        event.day_keys.every(
          key => overflowDays.has(key) && !visibleIdsByDay.get(key).has(event.id)
        )
      )
      .map(event => event.id)
  );

  const buttonsByAnchorId = new Map();

  overflowDays.forEach(key => {
    const dayEvents = eventsByDay.get(key);
    const hiddenCount = dayEvents.filter(event => hiddenIds.has(event.id)).length;

    if (hiddenCount === 0) {
      return;
    }

    // Anchor after the day's last event so the button renders below the
    // expanded list; hidden events collapse, keeping it under the 5th card.
    const anchor = dayEvents[dayEvents.length - 1];
    const buttons = buttonsByAnchorId.get(anchor.id) || [];
    buttons.push({
      kind: 'more',
      dayKey: key,
      remainingCount: hiddenCount,
      expanded: Boolean(expandedDays[key]),
      grid_start: dayKeys.indexOf(key) + 1,
      grid_span: 1,
    });
    buttonsByAnchorId.set(anchor.id, buttons);
  });

  const isHidden = event =>
    hiddenIds.has(event.id) && !event.day_keys.some(key => expandedDays[key]);
  const isCrowded = event =>
    event.day_keys.some(key => eventsByDay.get(key).length > VISIBLE_CALENDAR_EVENTS_LIMIT);

  const renderItems = [];
  placements.forEach(event => {
    renderItems.push({ kind: 'event', event, hidden: isHidden(event), crowded: isCrowded(event) });
    const buttons = buttonsByAnchorId.get(event.id);
    if (buttons) {
      renderItems.push(...buttons);
    }
  });

  return {
    placements,
    renderItems,
    hasOverflow: buttonsByAnchorId.size > 0,
  };
}

/**
 * Date labels for one calendar row.
 *
 * `referenceMonthKey` is the "YYYY-MM" the view is anchored to. A row outside it,
 * eg a week span that runs past the end of the month, names its own month so it's
 * obvious to the user which month the row is in.
 *
 * @param {object} event
 * @param {string} timezone
 * @param {{ referenceMonthKey?: string }} options
 * @returns {object}
 */
export function calendarEventRangeInfo(event, timezone = DEFAULT_TIMEZONE, options = {}) {
  const startKey = event.range_start_key || calendarEventDateKey(event, timezone);
  const endKey = event.range_end_key || calendarEventEndDateKey(event, timezone) || startKey;
  const start = dateKeyInfo(startKey, timezone);
  const end = dateKeyInfo(endKey, timezone);
  const isRange = startKey !== endKey;
  const currentYear = calendarEventCurrentYear(timezone);
  const referenceMonthKey = options.referenceMonthKey || '';
  const showMonth = Boolean(referenceMonthKey) && startKey.substring(0, 7) !== referenceMonthKey;

  return {
    start,
    end,
    isRange,
    isToday: dateKeyContainsToday(startKey, endKey, timezone),
    dayNameLabel: isRange ? `${start.dayName}–${end.dayName}` : start.dayName,
    dayNumberLabel: dayNumberLabel(start, end, isRange, showMonth),
    inlineDateLabel: isRange
      ? calendarRangeDateLabel(start, end, currentYear)
      : calendarDateLabel(start, currentYear),
  };
}

/**
 * Spoken date and time summary for a calendar event card.
 *
 * Every calendar view renders the date and the time outside the card's link, so
 * neither reaches the link's accessible name and neither is announced alongside
 * the event it belongs to. Cards repeat this string as visually hidden text
 * inside the link to put the schedule back with the event.
 *
 * @param {object} event
 * @param {string} timezone
 * @returns {string}
 */
export function calendarEventScheduleLabel(event, timezone = DEFAULT_TIMEZONE) {
  const { start, end, isRange } = calendarEventRangeInfo(event, timezone);
  const dateLabel = isRange
    ? [spokenDateLabel(start), spokenDateLabel(end)].filter(Boolean).join(' to ')
    : spokenDateLabel(start);
  const displayTime = calendarEventDisplayTime(event, timezone);
  const timeLabel = displayTime
    ? [displayTime.label, displayTime.timezoneLabel].filter(Boolean).join(' ')
    : '';

  return [dateLabel, timeLabel].filter(Boolean).join(', ');
}

export function calendarEventRangeKey(event, timezone = DEFAULT_TIMEZONE) {
  const startKey = event.range_start_key || calendarEventDateKey(event, timezone);
  const endKey = event.range_end_key || calendarEventEndDateKey(event, timezone) || startKey;

  return `${startKey}:${endKey}`;
}

export function isCalendarEventComplexSchedule(event, timezone = DEFAULT_TIMEZONE) {
  const startKey = event.range_start_key || calendarEventDateKey(event, timezone);
  const endKey = event.range_end_key || calendarEventEndDateKey(event, timezone);

  return startKey !== endKey && event.display_mode !== CALENDAR_EVENT_DISPLAY_MODES.dateOnly;
}

/**
 * Partitions occurrences into contiguous-day runs.
 *
 * Exposes occurrenceGroups so callers outside the calendar projections stay on
 * one definition of "consecutive days", including its reviews opt-out.
 *
 * @param {Array<object>} occurrences
 * @param {string} timezone
 * @param {{ skipRunMerging?: boolean }} options
 * @returns {Array<Array<object>>}
 */
export function calendarEventOccurrenceRuns(
  occurrences,
  timezone = DEFAULT_TIMEZONE,
  options = {}
) {
  return occurrenceGroups(occurrences, timezone, options);
}

function occurrencesForEvent(event) {
  if (Array.isArray(event.occurrences) && event.occurrences.length > 0) {
    return event.occurrences;
  }

  return [
    {
      id: event.id ?? null,
      gsd_service_api_id: event.gsd_service_api_id ?? null,
      start_time_utc: event.start_time_utc ?? null,
      end_time_utc: event.end_time_utc ?? null,
      start_time: event.start_time,
      end_time: event.end_time,
      start_date: event.start_date ?? null,
      end_date: event.end_date ?? null,
      display_mode: event.display_mode ?? CALENDAR_EVENT_DISPLAY_MODES.timed,
      location: event.location ?? null,
      location_override: event.location_override ?? null,
      location_url: event.location_url ?? null,
    },
  ];
}

function occurrenceRange(occurrence, timezone) {
  const startKey = calendarEventRangeStartKey(occurrence, timezone);
  const endKey = calendarEventRangeEndKey(occurrence, timezone) || startKey;

  return endKey < startKey ? { startKey, endKey: startKey } : { startKey, endKey };
}

function calendarEventRangeStartKey(event, timezone) {
  if (event?.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly) {
    return calendarEventDateKey(event, timezone);
  }

  return event?.start_date || calendarEventDateKey(event, timezone);
}

function calendarEventRangeEndKey(event, timezone) {
  if (event?.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly) {
    return calendarEventEndDateKey(event, timezone);
  }

  return event?.end_date || event?.start_date || calendarEventEndDateKey(event, timezone);
}

function occurrenceGroupsForEvent(event, timezone) {
  return occurrenceGroups(occurrencesForEvent(event), timezone, {
    skipRunMerging: event.filter_group === 'reviews',
  });
}

function listItemsForOccurrenceRun(event, occurrences, visibleRange, timezone) {
  const runRange = rangeForOccurrences(occurrences, timezone);

  // Show the run only when it overlaps the visible window, dropping past runs
  // and runs that belong to another month.
  if (
    !rangesOverlap(runRange.startKey, runRange.endKey, visibleRange.startKey, visibleRange.endKey)
  ) {
    return [];
  }

  const projected = eventForOccurrences(event, occurrences, timezone);
  // Clamp to the month for display, then split so a run reaching into another
  // month still renders one row per visible month.
  const displayStartKey = maxDateKey(runRange.startKey, visibleRange.monthStartKey);
  const displayEndKey = minDateKey(runRange.endKey, visibleRange.endKey);

  return splitRangeByMonth(displayStartKey, displayEndKey).map(segment =>
    projectedEventForVisibleSegment(projected, segment)
  );
}

function projectedEventForVisibleSegment(projected, segment) {
  const visibleEvent = {
    ...projected,
    id: `${projected.calendar_event_id}:${segment.startKey}:${segment.endKey}`,
    start_date: segment.startKey,
    end_date: segment.endKey,
    range_start_key: segment.startKey,
    range_end_key: segment.endKey,
  };

  if (visibleEvent.display_mode === CALENDAR_EVENT_DISPLAY_MODES.dateOnly) {
    return {
      ...visibleEvent,
      start_time_utc: `${segment.startKey} 00:00:00`,
      end_time_utc: `${segment.endKey} 23:59:59`,
      start_time: `${segment.startKey} 00:00:00`,
      end_time: `${segment.endKey} 23:59:59`,
    };
  }

  return visibleEvent;
}

function splitRangeByMonth(startKey, endKey) {
  const segments = [];
  let segmentStartKey = startKey;

  while (segmentStartKey <= endKey) {
    const monthEndKey = addDays(addMonths(`${segmentStartKey.substring(0, 7)}-01`, 1), -1);
    const segmentEndKey = minDateKey(endKey, monthEndKey);

    segments.push({
      startKey: segmentStartKey,
      endKey: segmentEndKey,
    });

    segmentStartKey = addDays(segmentEndKey, 1);
  }

  return segments;
}

function occurrenceGroups(occurrences, timezone, { skipRunMerging = false } = {}) {
  // Review events render one row per occurrence, so each occurrence stays its
  // own single-element group instead of merging consecutive days into a run.
  if (skipRunMerging) {
    return sortOccurrences(occurrences).map(occurrence => [occurrence]);
  }

  return sortOccurrences(occurrences)
    .reduce((groups, occurrence) => {
      const range = occurrenceRange(occurrence, timezone);
      const currentGroup = groups[groups.length - 1];

      if (!currentGroup || range.startKey > addDays(currentGroup.endKey, 1)) {
        groups.push({
          endKey: range.endKey,
          occurrences: [occurrence],
        });

        return groups;
      }

      currentGroup.endKey = range.endKey > currentGroup.endKey ? range.endKey : currentGroup.endKey;
      currentGroup.occurrences.push(occurrence);

      return groups;
    }, [])
    .map(group => group.occurrences);
}

function eventForOccurrences(event, occurrences, timezone) {
  const sortedOccurrences = sortOccurrences(occurrences);
  const firstOccurrence = sortedOccurrences[0];
  const range = rangeForOccurrences(sortedOccurrences, timezone);
  const occurrenceLocations = distinctValues(
    sortedOccurrences.map(occurrence => occurrence.location)
  );
  const locations = occurrenceLocations.length
    ? occurrenceLocations
    : distinctValues((event.locations || []).concat(event.location || []));

  return {
    ...event,
    id: `${event.id}:${range.startKey}:${range.endKey}`,
    calendar_event_id: event.id,
    start_time_utc: calendarEventStartTime(firstOccurrence),
    end_time_utc: latestEndTime(sortedOccurrences) || calendarEventEndTime(firstOccurrence),
    start_time: calendarEventStartTime(firstOccurrence),
    end_time: latestEndTime(sortedOccurrences) || calendarEventEndTime(firstOccurrence),
    start_date: range.startKey || firstOccurrence.start_date,
    end_date: range.endKey || latestEndDate(sortedOccurrences) || firstOccurrence.end_date,
    display_mode:
      firstOccurrence.display_mode || event.display_mode || CALENDAR_EVENT_DISPLAY_MODES.timed,
    location: locationWithZoom(locations.join(', ') || event.location || '', hasZoom(event)),
    locations,
    occurrence_date_key: range.startKey,
    range_start_key: range.startKey,
    range_end_key: range.endKey,
    visible_occurrences: sortedOccurrences,
  };
}

function eventForWeek(event, weekStartKey, weekEndKey, dayKeys = []) {
  const startsBeforeWeek = event.range_start_key < weekStartKey;
  const endsAfterWeek = event.range_end_key > weekEndKey;
  const visibleStartKey = startsBeforeWeek ? weekStartKey : event.range_start_key;
  const visibleEndKey = endsAfterWeek ? weekEndKey : event.range_end_key;
  const gridStart = daysBetween(weekStartKey, visibleStartKey) + 1;
  const gridSpan = daysBetween(visibleStartKey, visibleEndKey) + 1;

  return {
    ...event,
    id: `${event.id}:${weekStartKey}`,
    week_start_key: weekStartKey,
    grid_start: gridStart,
    grid_span: gridSpan,
    day_keys: dayKeys.slice(gridStart - 1, gridStart - 1 + gridSpan),
    starts_before_week: startsBeforeWeek,
    ends_after_week: endsAfterWeek,
    is_multi_day: event.range_start_key !== event.range_end_key,
  };
}

function visibleMonthRange(activeRange, timezone, showPastEvents) {
  const visibleRange = calendarVisibleDateRange(activeRange, timezone, showPastEvents);

  return {
    // startKey may be today (current month, hiding past events) so fully-past
    // events are filtered out; monthStartKey is always the first of the month
    // and bounds how far back the displayed range is clamped.
    startKey: visibleRange.start_after,
    monthStartKey: addMonths(visibleRange.start_before, -1),
    endKey: addDays(visibleRange.start_before, -1),
  };
}

function rangeForOccurrences(occurrences, timezone) {
  return occurrences.reduce(
    (range, occurrence) => {
      const occurrenceDateRange = occurrenceRange(occurrence, timezone);

      return {
        startKey:
          range.startKey === '' || occurrenceDateRange.startKey < range.startKey
            ? occurrenceDateRange.startKey
            : range.startKey,
        endKey:
          range.endKey === '' || occurrenceDateRange.endKey > range.endKey
            ? occurrenceDateRange.endKey
            : range.endKey,
      };
    },
    { startKey: '', endKey: '' }
  );
}

function rangesOverlap(leftStartKey, leftEndKey, rightStartKey, rightEndKey) {
  return Boolean(
    leftStartKey &&
      leftEndKey &&
      rightStartKey &&
      rightEndKey &&
      leftStartKey <= rightEndKey &&
      leftEndKey >= rightStartKey
  );
}

function minDateKey(leftKey, rightKey) {
  return leftKey <= rightKey ? leftKey : rightKey;
}

function maxDateKey(leftKey, rightKey) {
  return leftKey >= rightKey ? leftKey : rightKey;
}

function dateKeyContainsToday(startKey, endKey, timezone) {
  const todayKey = getCurrentCalendarDateKey(timezone);

  return Boolean(startKey && endKey && todayKey >= startKey && todayKey <= endKey);
}

function spokenDateLabel(day) {
  if (!day?.dateKey) {
    return '';
  }

  const [year, month, dayNumber] = day.dateKey.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, dayNumber, 12, 0, 0)).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function dayNumberLabel(start, end, isRange, showMonth) {
  if (!isRange) {
    return showMonth ? `${start.monthName} ${start.dayNumber}` : start.dayNumber;
  }

  const label = rangeNumberLabel(start, end);

  // A cross-month range already names both months.
  return showMonth && start.monthName === end.monthName ? `${start.monthName} ${label}` : label;
}

function rangeNumberLabel(start, end) {
  if (start.year !== end.year) {
    return `${start.monthName} ${start.dayNumber}, ${start.year}–${end.monthName} ${end.dayNumber}, ${end.year}`;
  }

  if (start.monthName !== end.monthName) {
    return `${start.monthName} ${start.dayNumber}–${end.monthName} ${end.dayNumber}`;
  }

  return `${start.dayNumber}–${end.dayNumber}`;
}

function sortOccurrences(occurrences) {
  return [...occurrences].sort((a, b) => {
    return calendarEventSortTimestamp(a) - calendarEventSortTimestamp(b);
  });
}

function latestEndTime(occurrences) {
  return occurrences.reduce((latest, occurrence) => {
    const endTime = calendarEventEndTime(occurrence);

    return latest === '' || String(endTime).localeCompare(latest) > 0 ? endTime : latest;
  }, '');
}

function latestEndDate(occurrences) {
  return occurrences.reduce((latest, occurrence) => {
    const endDate = occurrence.end_date || occurrence.start_date || '';

    return latest === '' || String(endDate).localeCompare(latest) > 0 ? endDate : latest;
  }, '');
}

function distinctValues(values) {
  return values.filter((value, index, list) => {
    const normalized = String(value || '').trim();

    return (
      normalized !== '' &&
      list.findIndex(item => String(item || '').trim() === normalized) === index
    );
  });
}

function sortProjectedEvents(events) {
  return [...events].sort((a, b) => {
    const dateCompare = String(a.range_start_key || '').localeCompare(
      String(b.range_start_key || '')
    );

    if (dateCompare !== 0) {
      return dateCompare;
    }

    return calendarEventSortTimestamp(a) - calendarEventSortTimestamp(b);
  });
}

function addMonths(dateKey, months) {
  const date = dateKeyToDate(dateKey);
  date.setUTCMonth(date.getUTCMonth() + months);

  return dateToKey(date);
}

function addDays(dateKey, days) {
  const date = dateKeyToDate(dateKey);
  date.setUTCDate(date.getUTCDate() + days);

  return dateToKey(date);
}

function daysBetween(startKey, endKey) {
  return Math.round((dateKeyToDate(endKey).getTime() - dateKeyToDate(startKey).getTime()) / DAY_MS);
}

function dateKeyToDate(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
}

function dateToKey(date) {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}
